import {
  Box,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  Condition,
  DiagnosticReport,
  Location,
  Organization,
  Practitioner,
} from "@medplum/fhirtypes";
import { toTitleCase } from "@metriport/shared";
import DOMPurify from "dompurify";
import {
  cleanBase64String,
  getBase64PdfOrImageType,
  isImageType,
  isPdfType,
} from "../../../shared-logic/base64-utils";
import { SimplePdfViewer } from "../../simple-pdf-viewer";
import { Report, getReportTypeByLoinc } from "./table-data";
import { decodeBase64AsUtf8 } from "../shared";

export function ReportContent({ encounterNote }: { encounterNote: Report }) {
  return (
    <Box w={"100%"}>
      <Box mb={4}>
        <TypeOfReport diagnosticReports={encounterNote.diagnosticReports} />
        <Participants participants={encounterNote.participants} />
        <Locations locations={encounterNote.locations} orgs={encounterNote.organizations} />
      </Box>
      <ReportNotes reports={encounterNote.diagnosticReports} />
    </Box>
  );
}

function TypeOfReport({ diagnosticReports }: { diagnosticReports: DiagnosticReport[] }) {
  const reportType = getReportTypeByLoinc(diagnosticReports);

  return (
    <>
      <Box display={"flex"}>
        <Text w={"120px"} m={0} mr={2} fontSize={18} fontWeight="bold">
          Type(s):
        </Text>
        <Box>
          <Text m={0} fontSize={18}>
            {reportType}
          </Text>
        </Box>
      </Box>
    </>
  );
}

function Participants({ participants }: { participants: Practitioner[] }) {
  return (
    <>
      <Box display={"flex"}>
        <Text w={"120px"} m={0} mr={2} fontSize={18} fontWeight="bold">
          Provider(s):
        </Text>
        <Box>
          {participants.map((participant, i) => (
            <Text key={i} m={0} fontSize={18}>
              {participant.name?.[0]?.given?.[0]} {participant.name?.[0]?.family}
              {i < participants.length - 1 ? ", " : ""}
            </Text>
          ))}
        </Box>
      </Box>
    </>
  );
}

function Locations({
  locations,
  orgs,
}: {
  locations?: Location[] | undefined;
  orgs?: Organization[] | undefined;
}) {
  return (
    <>
      <Box display={"flex"}>
        <Text w={"120px"} m={0} mr={2} fontSize={18} fontWeight="bold">
          Location(s):
        </Text>
        <Box>
          {locations?.map((location, i) => (
            <Text key={i} m={0} fontSize={18}>
              {location.name}
              {i < locations.length - 1 ? ", " : ""}
            </Text>
          ))}
          {orgs?.map((org, i) => (
            <Text key={i} m={0} fontSize={18}>
              {org.name}
              {i < orgs.length - 1 ? ", " : ""}
            </Text>
          ))}
        </Box>
      </Box>
    </>
  );
}

export function getDiagnosisText(diagnosis: Condition): string | undefined {
  const displayText = diagnosis.code?.coding
    ?.flatMap(coding => coding.display?.trim() || [])
    .join(", ");
  return (
    diagnosis.code?.text ??
    (displayText && displayText.length ? toTitleCase(displayText) : undefined)
  );
}

function ReportNotes({ reports }: { reports: DiagnosticReport[] }) {
  return (
    <Box>
      {reports?.map((report, i) => (
        <ReportNote key={i} report={report} />
      ))}
    </Box>
  );
}

export function ReportNote({ report }: { report: DiagnosticReport }) {
  const cleanNotes = getCleanNotesFromReport(report);
  const borderColor = useColorModeValue("gray.200", "gray.700");

  if (!cleanNotes || cleanNotes.length === 0) {
    return null;
  }

  return (
    <>
      {cleanNotes.map((cleanNote, index) => {
        return (
          <Box key={index} display={"flex"} flexDirection="column">
            <Box
              px={4}
              py={2}
              border="solid"
              borderRadius={10}
              borderColor={borderColor}
              mb={5}
              whiteSpace={"pre-line"}
            >
              <NoteContent cleanNote={cleanNote} />
            </Box>
          </Box>
        );
      })}
    </>
  );
}

function isHasTableTag(cleanNote: string): boolean {
  return cleanNote.includes("<table") && cleanNote.includes("</table>");
}

function parseXML(cleanNote: string): Document {
  const parser = new DOMParser();
  return parser.parseFromString(cleanNote, "application/xml");
}

function handleHtml(parsedNote: string | null, originalNote: string): string {
  if (parsedNote?.includes("This page contains the following errors")) {
    return originalNote;
  }
  return parsedNote || originalNote;
}

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html);
}

export function getCleanNotesFromReport(report: DiagnosticReport): string[] {
  const presentedForms = report.presentedForm || [];
  return presentedForms
    .map(form => {
      if (!form.data) return "";

      const cleanData = cleanBase64String(form.data);
      if (!cleanData) return "";

      const fileType = getBase64PdfOrImageType(cleanData);

      // Keep these file types encoded, handle later in the component
      if (isImageType(fileType) || isPdfType(fileType)) {
        return cleanData;
      }

      // Decode and clean text/HTML content
      try {
        return cleanUpNote(decodeBase64AsUtf8(cleanData));
      } catch (error) {
        console.error("Failed to decode presentedForm data:", error);
        return "";
      }
    })
    .filter(note => note.trim().length > 0);
}

function cleanUpNote(note: string): string {
  return note
    .trim()
    .replace(/<ID>.*?<\/ID>/g, "")
    .replace(/Â/g, " ")
    .replace(/<styleCode>.*?<\/styleCode>/g, "")
    .replace(/<colspan>.*?<\/colspan>/g, "")
    .replace(/<script>.*?<\/script>/g, "");
}

function NoteAsTable({
  xmlDocument,
  sanitizedNote,
}: {
  xmlDocument: Document;
  sanitizedNote: string;
}) {
  // if note can be a text + a table, then we would need to handle that.
  // (v2) Discover such case with more comprehensive data on production!
  const captionElement = xmlDocument.querySelector("caption");
  const tableElement = xmlDocument.querySelector("table");

  if (!tableElement) return <div dangerouslySetInnerHTML={{ __html: sanitizedNote }} />;

  const thead = tableElement.querySelector("thead");
  const tbody = tableElement.querySelector("tbody");
  const tfoot = tableElement.querySelector("tfoot");

  if (!thead && !tbody) return <div dangerouslySetInnerHTML={{ __html: sanitizedNote }} />;

  return (
    <TableContainer
      overflowX="scroll"
      css={{
        "&::-webkit-scrollbar": {
          WebkitAppearance: "none",
          height: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: "4px",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      {captionElement && (
        <Heading as="h4" size="sm" paddingX={4} textAlign="left" my={4}>
          {captionElement.textContent?.trim()}
        </Heading>
      )}
      <Table variant="simple" size="sm">
        {thead && (
          <Thead>
            {Array.from(thead.querySelectorAll("tr")).map((row, rowIndex) => (
              <Tr key={`thead-${rowIndex}`}>
                {Array.from(row.children).map((cell, cellIndex) => (
                  <Th key={`thead-${rowIndex}-${cellIndex}`}>{cell.textContent?.trim()}</Th>
                ))}
              </Tr>
            ))}
          </Thead>
        )}
        {tbody && (
          <Tbody>
            {Array.from(tbody.querySelectorAll("tr")).map((row, rowIndex) => (
              <Tr key={`tbody-${rowIndex}`}>
                {Array.from(row.children).map((cell, cellIndex) => {
                  const isHeader = cell.tagName.toLowerCase() === "th";
                  return isHeader ? (
                    <Th key={`tbody-${rowIndex}-${cellIndex}`}>{cell.textContent?.trim()}</Th>
                  ) : (
                    <Td key={`tbody-${rowIndex}-${cellIndex}`}>{cell.textContent?.trim()}</Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        )}
        {tfoot && (
          <Tfoot>
            {Array.from(tfoot.querySelectorAll("tr")).map((row, rowIndex) => (
              <Tr key={`tfoot-${rowIndex}`}>
                {Array.from(row.children).map((cell, cellIndex) => (
                  <Th key={`tfoot-${rowIndex}-${cellIndex}`}>{cell.textContent?.trim()}</Th>
                ))}
              </Tr>
            ))}
          </Tfoot>
        )}
      </Table>
    </TableContainer>
  );
}

function NoteContent({ cleanNote }: { cleanNote: string }) {
  // Detect the file type from base64 data
  const detectedType = getBase64PdfOrImageType(cleanNote);
  const isImage = isImageType(detectedType);
  const isPdf = isPdfType(detectedType);
  const isText = !isPdf && !isImage;

  // Only parse HTML/XML for text content
  const isNoteAsTable = isText && isHasTableTag(cleanNote);
  const xmlDocument = isText ? parseXML(cleanNote) : null;
  const parsedNote = xmlDocument?.documentElement?.textContent ?? null;
  const sanitizedNote = isText ? sanitizeHtml(handleHtml(parsedNote, cleanNote)) : "";

  if (isPdf) {
    return <SimplePdfViewer base64Content={cleanNote} />;
  }

  if (isImage) {
    return (
      <img
        src={`data:${detectedType};base64,${cleanNote}`}
        alt={`Medical report image - ${detectedType}`}
        style={{ maxWidth: "100%", height: "auto" }}
      />
    );
  }

  if (isNoteAsTable && xmlDocument) {
    return (
      <NoteAsTable
        // NOTE: Passing the non-sanitized note as Document for tables
        // to ensure proper rendering. We do strip all <script> tags, so should be safe.
        xmlDocument={xmlDocument}
        sanitizedNote={sanitizedNote}
      />
    );
  }

  return <div dangerouslySetInnerHTML={{ __html: sanitizedNote }} />;
}
